import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper} from '@mui/material';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { MyLocation, Search, LocationOn } from '@mui/icons-material';
import { ModalAcceptButton } from '../../../constants/component.constants';
import { issueWarnMessage, issueSuccessMessage } from '../../../actions/message';

// Static libraries array to avoid LoadScript reloading
const LIBRARIES = ['marker'];

const LocationPickerModal = ({ open, onClose, onConfirm, initialLocation = null }) => {
    // Memoize the load script configuration to prevent recreation
    const loadScriptConfig = useMemo(() => ({
        googleMapsApiKey: "AIzaSyAi5vlmbshza2_JkvFmF7w2EHmP0Ez12GY",
        libraries: LIBRARIES,
        // preventGoogleFontsLoading: true
    }), []);

    const { isLoaded, loadError } = useLoadScript(loadScriptConfig);

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [mapCenter, setMapCenter] = useState({ lat: -1.286389, lng: 36.817223 }); // Default to Nairobi
    const [map, setMap] = useState(null);
    const advancedMarkerRef = useRef(null);
    const isNavigatingRef = useRef(false);
    
    // Search functionality state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [originalGeocodingResults, setOriginalGeocodingResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isProcessingResult, setIsProcessingResult] = useState(false);

    // Initialize with provided location or default
    useEffect(() => {
        if (open) {
            if (initialLocation && initialLocation.lat && initialLocation.lng) {
                setSelectedLocation(initialLocation);
                setMapCenter(initialLocation);
            } else {
                setSelectedLocation(null);
                setMapCenter({ lat: -1.286389, lng: 36.817223 }); // Reset to default
            }
        }
    }, [initialLocation, open]);

    // No need for Places API services with geocoding approach

    // Cleanup when modal closes
    useEffect(() => {
        if (!open) {
            setMap(null);
            setIsLoadingLocation(false);
            setSearchQuery('');
            setSearchResults([]);
            setOriginalGeocodingResults([]);
            setIsSearching(false);
            setIsProcessingResult(false);
            if (advancedMarkerRef.current) {
                advancedMarkerRef.current.map = null;
                advancedMarkerRef.current = null;
            }
        }
    }, [open]);

    // No need for useEffect map navigation - handled in onLoad and search result click

    // Create AdvancedMarkerElement when location is selected
    useEffect(() => {
        if (selectedLocation && map && window.google?.maps?.marker?.AdvancedMarkerElement) {
            // Clean up existing marker
            if (advancedMarkerRef.current) {
                advancedMarkerRef.current.map = null;
            }

            // Create the advanced marker with default pin
            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                map: map,
                position: selectedLocation,
                gmpDraggable: true
            });

            // Add drag event listener
            marker.addListener('dragend', (event) => {
                const lat = event.latLng.lat();
                const lng = event.latLng.lng();
                setSelectedLocation({ lat, lng });
            });

            advancedMarkerRef.current = marker;
        }
    }, [selectedLocation, map]);

    const handleMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
        
        // Set initial map center when map loads
        if (mapCenter) {
            mapInstance.setCenter(mapCenter);
            mapInstance.setZoom(selectedLocation ? 15 : 11);
        }
    }, [mapCenter, selectedLocation]);

    const handleMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setSelectedLocation({ lat, lng });
    }, []);

    // Search functionality using Geocoding API
    const handleSearch = useCallback(async () => {
        if (!searchQuery.trim() || !window.google?.maps) return;

        setIsSearching(true);
        setSearchResults([]);

        try {
            const geocoder = new window.google.maps.Geocoder();
            
            geocoder.geocode(
                {
                    address: searchQuery,
                    componentRestrictions: { country: 'KE' }, // Restrict to Kenya
                    region: 'KE'
                },
                (results, status) => {
                    
                    if (status === window.google.maps.GeocoderStatus.OK && results) {
                        // Store original geocoding results for later use
                        setOriginalGeocodingResults(results);
                        
                        // Transform geocoding results to match our expected format
                        const transformedResults = results.map((result, index) => {
                            return {
                                place_id: `geocode_${index}_${Date.now()}`,
                                description: result.formatted_address,
                                structured_formatting: {
                                    main_text: result.address_components[0]?.long_name || result.formatted_address,
                                    secondary_text: result.formatted_address
                                }
                            };
                        });
                        setSearchResults(transformedResults);
                    } else {
                        setSearchResults([]);
                        setOriginalGeocodingResults([]);
                        issueWarnMessage('No locations found for your search.');
                    }
                    setIsSearching(false);
                }
            );
        } catch (error) {
            console.error('Search error:', error);
            issueWarnMessage('Error searching for locations.');
            setIsSearching(false);
        }
    }, [searchQuery]);

    const handleSearchResultClick = useCallback((placeId, result) => {
        // Prevent multiple clicks while processing
        if (isProcessingResult) {
            return;
        }
        
        // Extract coordinates from the geocoding result
        if (result && result.geometry && result.geometry.location) {
            setIsProcessingResult(true);
            
            const location = {
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng()
            };
            
            // Clear search results and query first to prevent re-triggering
            setSearchResults([]);
            setSearchQuery('');
            
            // Set the selected location
            setSelectedLocation(location);
            setMapCenter(location);
            
            // Navigate the map directly with proper timing
            if (map) {
                isNavigatingRef.current = true;
                
                // Use a small delay to ensure the map is fully ready
                setTimeout(() => {
                    if (map && window.google?.maps?.LatLng) {
                        
                        // Create a LatLng object for proper navigation
                        const latLng = new window.google.maps.LatLng(location.lat, location.lng);
                        
                        // Set the map center and zoom
                        map.setCenter(latLng);
                        map.setZoom(15);
                        
                        // Also try panTo as backup
                        map.panTo(latLng);
                        
                        // Force a resize event to ensure the map updates properly
                        if (window.google?.maps?.event) {
                            window.google.maps.event.trigger(map, 'resize');
                        }
                        
                        
                        // Reset navigation flag after a longer delay to prevent interference
                        setTimeout(() => {
                            isNavigatingRef.current = false;
                        }, 500);
                    } else {
                        console.error('Map or Google Maps API not ready for navigation'); // Debug log
                        isNavigatingRef.current = false;
                    }
                    
                    // Reset processing flag after navigation
                    setIsProcessingResult(false);
                }, 100);
            } else {
                setIsProcessingResult(false);
            }
            
            issueSuccessMessage(`Location set to: ${result.formatted_address}`);
        } else {
            console.error('Invalid result structure:', result); // Debug log
            issueWarnMessage('Could not get details for the selected location.');
            setIsProcessingResult(false);
        }
    }, [map, isProcessingResult]);

    const handleSearchInputChange = useCallback((event) => {
        setSearchQuery(event.target.value);
        if (event.target.value.trim() === '') {
            setSearchResults([]);
        }
    }, []);


    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            issueWarnMessage('Geolocation is not supported by your browser.');
            return;
        }

        setIsLoadingLocation(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                const location = { lat, lng };
                
                setSelectedLocation(location);
                setMapCenter(location);
                
                // Pan map to current location
                if (map) {
                    map.panTo(location);
                    map.setZoom(15);
                }
                
                setIsLoadingLocation(false);
                issueSuccessMessage('Current location detected');
            },
            (error) => {
                setIsLoadingLocation(false);
                let errorMessage = 'Unable to retrieve your location.';
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access denied by user.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out.';
                        break;
                    default:
                        errorMessage = 'Unable to retrieve your location.';
                        break;
                }
                issueWarnMessage(errorMessage);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000 // 5 minutes
            }
        );
    };

    const handleConfirm = () => {
        if (selectedLocation) {
            onConfirm(selectedLocation.lat, selectedLocation.lng);
            onClose();
        }
    };

    const handleCancel = () => {
        onClose();
    };

    // Handle load error
    if (loadError) {
        return (
            <Dialog 
                open={open} 
                onClose={handleCancel}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        minHeight: '500px'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: '#037171', 
                    color: 'white',
                    textAlign: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                }}>
                    Select Location
                </DialogTitle>
                <DialogContent sx={{ padding: '20px', textAlign: 'center' }}>
                    <Typography variant="h6" color="error">
                        Error loading Google Maps
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: '8px' }}>
                        Please refresh the page and try again.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 20px', gap: '8px' }}>
                    <Button 
                        onClick={handleCancel}
                        variant="outlined"
                        sx={{
                            borderColor: '#ccc',
                            color: '#666',
                            '&:hover': {
                                borderColor: '#999',
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    const mapContainerStyle = {
        width: '100%',
        height: '400px',
        borderRadius: '8px'
    };


    return (
        <Dialog 
                open={open} 
                onClose={handleCancel}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        minHeight: '500px'
                    }
                }}
            >
            <DialogTitle sx={{ 
                backgroundColor: '#037171', 
                color: 'white',
                textAlign: 'center',
                fontSize: '1.2rem',
                fontWeight: 'bold'
            }}>
                Select Location
            </DialogTitle>
            
            <DialogContent sx={{ padding: '20px' }}>
                <Box sx={{ marginBottom: '16px' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '8px' }}>
                        Search for a location, click on the map, or use your current location.
                    </Typography>
                    
                    {/* Search Input */}
                    <Box sx={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <TextField
                            fullWidth
                            placeholder="Search for a location (e.g., Nairobi, Mombasa, Westlands)"
                            value={searchQuery}
                            onChange={handleSearchInputChange}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch();
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={!searchQuery.trim() || isSearching}
                            sx={{
                                backgroundColor: '#037171',
                                '&:hover': {
                                    backgroundColor: '#2d9f93'
                                },
                                minWidth: '100px'
                            }}
                        >
                            {isSearching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
                        </Button>
                    </Box>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <Paper sx={{ 
                            maxHeight: '200px', 
                            overflow: 'auto', 
                            marginBottom: '12px',
                            border: '1px solid #e0e0e0'
                        }}>
                            <List dense>
                                {searchResults.map((result, index) => (
                                    <ListItem
                                        key={result.place_id}
                                        button
                                        onClick={() => handleSearchResultClick(result.place_id, originalGeocodingResults[index])}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5'
                                            }
                                        }}
                                    >
                                        <LocationOn sx={{ marginRight: '8px', color: '#037171' }} />
                                        <ListItemText
                                            primary={result.structured_formatting?.main_text || result.description}
                                            secondary={result.structured_formatting?.secondary_text}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                    
                    <Button
                        variant="outlined"
                        startIcon={isLoadingLocation ? <CircularProgress size={16} /> : <MyLocation />}
                        onClick={handleUseCurrentLocation}
                        disabled={isLoadingLocation}
                        sx={{
                            borderColor: '#037171',
                            color: '#037171',
                            '&:hover': {
                                borderColor: '#2d9f93',
                                backgroundColor: '#f0f9f8'
                            }
                        }}
                    >
                        {isLoadingLocation ? 'Getting Location...' : 'Use Current Location'}
                    </Button>
                </Box>

                {selectedLocation && (
                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '8px' }}>
                            Selected coordinates:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <Chip 
                                label={`Lat: ${selectedLocation.lat.toFixed(6)}`}
                                color="primary"
                                size="small"
                            />
                            <Chip 
                                label={`Lng: ${selectedLocation.lng.toFixed(6)}`}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    </Box>
                )}

                {!isLoaded ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '400px',
                        gap: '16px'
                    }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="text.secondary">
                            Loading Google Maps...
                        </Typography>
                    </Box>
                ) : (
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        zoom={selectedLocation ? 15 : 11}
                        onClick={handleMapClick}
                        onLoad={handleMapLoad}
                        options={{
                            mapId: 'location-picker-map',
                            mapTypeControl: true,
                            streetViewControl: true,
                            fullscreenControl: true,
                            zoomControl: true,
                            restriction: {
                                latLngBounds: {
                                    north: 5.5,
                                    south: -5.0,
                                    west: 33.0,
                                    east: 42.0
                                },
                                strictBounds: false
                            }
                        }}
                    >
                    </GoogleMap>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: '16px 20px', gap: '8px' }}>
                <Button 
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                        borderColor: '#ccc',
                        color: '#666',
                        '&:hover': {
                            borderColor: '#999',
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                >
                    Cancel
                </Button>
                <ModalAcceptButton
                    onClick={handleConfirm}
                    disabled={!selectedLocation}
                    sx={{
                        backgroundColor: selectedLocation ? '#037171' : '#ccc',
                        '&:hover': {
                            backgroundColor: selectedLocation ? '#2d9f93' : '#ccc'
                        }
                    }}
                >
                    Confirm Location
                </ModalAcceptButton>
            </DialogActions>
        </Dialog>
    );
};

export default LocationPickerModal;
